/**
 * Custom font-aware combobox. Wraps a hidden native `<select>` (kept as the
 * source of truth + form/keyboard semantics + existing `change` event
 * wiring) and renders:
 *
 *   - A trigger button styled in the currently-selected font's typeface.
 *   - A popover listbox where each option is rendered in its OWN typeface
 *     so the user can SEE what they're picking. Native <option style="…">
 *     is unreliable across browsers (Safari ignores it in the closed
 *     state and inconsistently when open), so we render our own buttons.
 *
 * Designed to be a drop-in upgrade: `upgradeToFontCombobox(selectEl, opts)`
 * leaves the select element in place (visually hidden), so existing
 * callers can keep doing `selectEl.value = '…'` and
 * `selectEl.addEventListener('change', …)` and everything continues to
 * work — the combobox stays visually in sync.
 *
 * Lazy font loading: the combobox eagerly loads only the selected font, then
 * loads the visible/nearby option window as the list scrolls.
 */

import { ensureUIFontLoaded, FONT_GROUPS, getGenericFallback } from '../theme/font-selector.js';

const ICON_CHEVRON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/></svg>';
const ICON_CHECK =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>';

let _comboboxCounter = 0;

/**
 * Upgrade a `<select>` to a font-aware combobox. Returns an object with a
 * `destroy()` method to tear down listeners and DOM additions.
 *
 * Two render modes:
 *   - 'font' (default): each option's label is rendered in its OWN typeface
 *     (option.value is the font name). Listbox is grouped by Sans / Serif /
 *     Monospace section headings unless `groupByFontClass: false`.
 *
 *   - 'weight': each option's label is rendered at its OWN weight (the
 *     option.value is the numeric weight, e.g. "300", "600"). Listbox
 *     items inherit `font-family` from `fontFamilyResolver()` so the
 *     user sees what each weight looks like in the heading font they
 *     just picked. Section headings are suppressed in this mode.
 *
 * @param {HTMLSelectElement} selectEl
 * @param {Object} [opts]
 * @param {'font'|'weight'} [opts.mode='font']
 * @param {boolean} [opts.groupByFontClass=true]      — applies to 'font' mode only
 * @param {() => string} [opts.fontFamilyResolver]    — required for 'weight' mode
 * @param {string}  [opts.placeholder]                — overridden trigger label when no value
 */
export function upgradeToFontCombobox(selectEl, opts = {}) {
  if (!(selectEl instanceof HTMLSelectElement)) return null;
  if (selectEl.dataset.pbFontCombobox === 'attached') return null;
  selectEl.dataset.pbFontCombobox = 'attached';

  const id = ++_comboboxCounter;
  const listboxId = `pb-fcb-listbox-${id}`;

  // Wrap: the trigger replaces the select visually; the select stays in the
  // DOM right after the trigger (visually hidden) so form semantics + the
  // existing change-event flow keep working.
  const wrap = document.createElement('div');
  wrap.className = 'pb-fcb';
  selectEl.parentNode.insertBefore(wrap, selectEl);
  wrap.appendChild(selectEl);
  selectEl.classList.add('pb-fcb-native');

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'pb-fcb-trigger';
  trigger.setAttribute('role', 'combobox');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', listboxId);

  const triggerLabel = document.createElement('span');
  triggerLabel.className = 'pb-fcb-trigger-label';
  trigger.appendChild(triggerLabel);

  const triggerIcon = document.createElement('span');
  triggerIcon.className = 'pb-fcb-trigger-icon';
  triggerIcon.innerHTML = ICON_CHEVRON;
  triggerIcon.setAttribute('aria-hidden', 'true');
  trigger.appendChild(triggerIcon);

  wrap.insertBefore(trigger, selectEl);

  const mode = opts.mode === 'weight' ? 'weight' : 'font';

  // ── Sync trigger to native select ──────────────────────────────────────
  function syncTrigger() {
    const selectedOption = selectEl.selectedOptions[0] || null;
    const rawValue = selectEl.value || '';
    const val = rawValue || opts.placeholder || '';
    if (mode === 'weight') {
      // Show the weight NAME ("Semi Bold"), not the numeric value.
      triggerLabel.textContent = selectedOption ? selectedOption.textContent || val : val;
      const family = typeof opts.fontFamilyResolver === 'function' ? opts.fontFamilyResolver() : '';
      if (family) {
        ensureUIFontLoaded(family).then((ok) => {
          trigger.classList.toggle('pb-fcb-trigger--font-error', !ok);
          if (!ok) trigger.dataset.tooltip = `Could not load ${family}; showing fallback`;
          else if (trigger.title?.startsWith('Could not load ')) trigger.removeAttribute('title');
        });
        const fallback = getGenericFallback(family);
        triggerLabel.style.fontFamily = `'${family}', ${fallback}`;
      } else {
        triggerLabel.style.fontFamily = '';
      }
      triggerLabel.style.fontWeight = val || '';
      return;
    }
    triggerLabel.textContent = selectedOption ? selectedOption.textContent || val : val;
    // Style the trigger in the chosen font so the user sees their pick
    // in its own typeface even when the listbox is closed.
    if (rawValue) {
      ensureUIFontLoaded(rawValue).then((ok) => {
        trigger.classList.toggle('pb-fcb-trigger--font-error', !ok);
        if (!ok) trigger.dataset.tooltip = `Could not load ${rawValue}; showing fallback`;
        else if (trigger.title?.startsWith('Could not load ')) trigger.removeAttribute('title');
      });
      const fallback = getGenericFallback(rawValue);
      triggerLabel.style.fontFamily = `'${rawValue}', ${fallback}`;
    } else {
      triggerLabel.style.fontFamily = '';
    }
  }
  syncTrigger();

  // The native select dispatches `change` when callers set `selectEl.value`
  // and then call `.dispatchEvent(new Event('change'))`, OR when the user
  // interacts directly. Either way, we want the trigger to stay in sync.
  selectEl.addEventListener('change', syncTrigger);

  // ── Listbox (lazy: built on first open) ────────────────────────────────
  let listboxEl = null;
  let outsideClickHandler = null;
  let keydownHandler = null;
  let scrollLoadRaf = null;
  let activeIndex = -1;

  const onListboxScroll = () => {
    if (scrollLoadRaf) cancelAnimationFrame(scrollLoadRaf);
    scrollLoadRaf = requestAnimationFrame(() => {
      scrollLoadRaf = null;
      loadVisibleOptions();
    });
  };

  function loadFontForItem(item) {
    if (mode !== 'font' || !item || item.dataset.fontLoadStarted === 'true') return;
    const family = item.dataset.value || '';
    if (!family) return;
    item.dataset.fontLoadStarted = 'true';
    item.classList.add('pb-fcb-option--font-loading');
    ensureUIFontLoaded(family).then((ok) => {
      item.classList.remove('pb-fcb-option--font-loading');
      item.classList.toggle('pb-fcb-option--font-error', !ok);
      if (!ok) {
        item.dataset.tooltip = `Could not load ${family}; showing fallback`;
        item.setAttribute('aria-label', `${family}, font unavailable`);
      }
    });
  }

  function loadOptionWindow(centerIndex = activeIndex) {
    if (mode !== 'font' || !listboxEl) return;
    const items = Array.from(listboxEl.querySelectorAll('.pb-fcb-option'));
    if (!items.length) return;
    const center = Number.isFinite(centerIndex) && centerIndex >= 0 ? centerIndex : 0;
    const start = Math.max(0, center - 6);
    const end = Math.min(items.length, center + 7);
    for (let i = start; i < end; i++) loadFontForItem(items[i]);
  }

  function loadVisibleOptions() {
    if (mode !== 'font' || !listboxEl) return;
    const items = Array.from(listboxEl.querySelectorAll('.pb-fcb-option'));
    const listRect = listboxEl.getBoundingClientRect();
    if (!listRect.height) {
      loadOptionWindow();
      return;
    }
    for (const item of items) {
      const rect = item.getBoundingClientRect();
      if (rect.bottom >= listRect.top - 80 && rect.top <= listRect.bottom + 80) {
        loadFontForItem(item);
      }
    }
  }

  function buildListbox() {
    if (listboxEl) return listboxEl;
    listboxEl = document.createElement('div');
    listboxEl.id = listboxId;
    listboxEl.className = 'pb-fcb-listbox';
    listboxEl.setAttribute('role', 'listbox');
    listboxEl.tabIndex = -1;
    listboxEl.hidden = true;

    // Collect option groups. 'font' mode groups by Sans/Serif/Mono when the
    // catalog map has a hit; 'weight' mode renders flat (only 7 options,
    // group headings would be noise).
    const groupByClass = mode === 'font' && opts.groupByFontClass !== false;
    const sectionOf = new Map();
    if (groupByClass) {
      for (const [groupName, names] of Object.entries(FONT_GROUPS)) {
        for (const n of names) sectionOf.set(n, groupName);
      }
    }

    const groups = new Map(); // groupName -> [optionEls]
    for (const opt of selectEl.options) {
      const group = groupByClass ? sectionOf.get(opt.value) || 'Other' : 'All';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(opt);
    }

    for (const [groupName, optionList] of groups) {
      if (groupByClass) {
        const heading = document.createElement('div');
        heading.className = 'pb-fcb-listbox-section-heading';
        heading.textContent = groupName;
        listboxEl.appendChild(heading);
      }
      for (const opt of optionList) {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'pb-fcb-option';
        item.id = `${listboxId}-option-${listboxEl.querySelectorAll('.pb-fcb-option').length}`;
        item.setAttribute('role', 'option');
        item.dataset.value = opt.value;
        item.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
        if (opt.selected) item.classList.add('pb-fcb-option--selected');

        const label = document.createElement('span');
        label.className = 'pb-fcb-option-label';
        label.textContent = opt.textContent || opt.value;
        if (mode === 'weight') {
          // Render the label at its own weight + in the heading font (so
          // "Bold Bevellier" actually shows what bold Bevellier looks like).
          const family =
            typeof opts.fontFamilyResolver === 'function' ? opts.fontFamilyResolver() : '';
          if (family) {
            const fallback = getGenericFallback(family);
            label.style.fontFamily = `'${family}', ${fallback}`;
          }
          label.style.fontWeight = opt.value;
        } else if (opt.value) {
          const fallback = getGenericFallback(opt.value);
          label.style.fontFamily = `'${opt.value}', ${fallback}`;
        }
        item.appendChild(label);

        const check = document.createElement('span');
        check.className = 'pb-fcb-option-check';
        check.innerHTML = ICON_CHECK;
        check.setAttribute('aria-hidden', 'true');
        item.appendChild(check);

        item.addEventListener('click', () => {
          setValue(opt.value);
          closeListbox(/* restoreFocus */ true);
        });
        item.addEventListener('mouseenter', () => {
          setActiveIndex(getOptionIndex(item), { focus: false, scroll: false });
          loadFontForItem(item);
        });
        listboxEl.appendChild(item);
      }
    }

    wrap.appendChild(listboxEl);
    listboxEl.addEventListener('scroll', onListboxScroll, { passive: true });
    return listboxEl;
  }

  function getOptionIndex(itemEl) {
    return Array.from(listboxEl.querySelectorAll('.pb-fcb-option')).indexOf(itemEl);
  }

  function setActiveIndex(index, { focus = true, scroll = true } = {}) {
    const items = listboxEl.querySelectorAll('.pb-fcb-option');
    if (!items.length) return;
    activeIndex = Math.max(0, Math.min(index, items.length - 1));
    items.forEach((el, i) => {
      el.classList.toggle('pb-fcb-option--active', i === activeIndex);
      if (i === activeIndex) {
        trigger.setAttribute('aria-activedescendant', el.id);
        if (scroll && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
        if (focus) el.focus({ preventScroll: true });
        loadFontForItem(el);
      }
    });
  }

  function refreshSelectedHighlight() {
    if (!listboxEl) return;
    const currentValue = selectEl.value;
    listboxEl.querySelectorAll('.pb-fcb-option').forEach((el) => {
      const isSel = el.dataset.value === currentValue;
      el.classList.toggle('pb-fcb-option--selected', isSel);
      el.setAttribute('aria-selected', isSel ? 'true' : 'false');
    });
  }

  function setValue(newValue) {
    if (selectEl.value === newValue) return;
    selectEl.value = newValue;
    selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    refreshSelectedHighlight();
    syncTrigger();
  }

  function openListbox() {
    buildListbox();
    listboxEl.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    wrap.classList.add('pb-fcb--open');

    // Move active index to the currently-selected option so arrow-down doesn't
    // jump to the top of the list.
    const items = Array.from(listboxEl.querySelectorAll('.pb-fcb-option'));
    const selectedIdx = items.findIndex((el) => el.dataset.value === selectEl.value);
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
    loadOptionWindow(selectedIdx >= 0 ? selectedIdx : 0);
    requestAnimationFrame(loadVisibleOptions);

    // Outside click to close.
    outsideClickHandler = (e) => {
      if (wrap.contains(e.target)) return;
      closeListbox(false);
    };
    document.addEventListener('pointerdown', outsideClickHandler);

    // Keyboard nav.
    keydownHandler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(activeIndex + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(activeIndex - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveIndex(Infinity);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const items2 = listboxEl.querySelectorAll('.pb-fcb-option');
        const target = items2[activeIndex];
        if (target) {
          setValue(target.dataset.value);
          closeListbox(true);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeListbox(true);
      } else if (e.key === 'Tab') {
        closeListbox(false);
      }
    };
    wrap.addEventListener('keydown', keydownHandler);
  }

  function closeListbox(restoreFocus) {
    if (!listboxEl || listboxEl.hidden) return;
    listboxEl.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    wrap.classList.remove('pb-fcb--open');
    trigger.removeAttribute('aria-activedescendant');
    if (outsideClickHandler) {
      document.removeEventListener('pointerdown', outsideClickHandler);
      outsideClickHandler = null;
    }
    if (keydownHandler) {
      wrap.removeEventListener('keydown', keydownHandler);
      keydownHandler = null;
    }
    if (restoreFocus) trigger.focus();
  }

  trigger.addEventListener('click', () => {
    const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
    if (willOpen) openListbox();
    else closeListbox(true);
  });
  // Open on ArrowDown / Enter / Space when the trigger has focus and the
  // listbox is closed (keyboard convenience).
  trigger.addEventListener('keydown', (e) => {
    if (trigger.getAttribute('aria-expanded') === 'true') return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openListbox();
    }
  });

  /**
   * Re-render the listbox + trigger. Use when the typography context the
   * combobox depends on has changed externally (e.g. weight-mode combobox
   * needs to re-render after the heading font is swapped, so the listbox
   * items show in the new font).
   */
  function refresh() {
    if (listboxEl) {
      const wasOpen = !listboxEl.hidden;
      listboxEl.removeEventListener('scroll', onListboxScroll);
      listboxEl.remove();
      listboxEl = null;
      if (wasOpen) openListbox();
    }
    syncTrigger();
  }

  function destroy() {
    closeListbox(false);
    if (scrollLoadRaf) cancelAnimationFrame(scrollLoadRaf);
    if (listboxEl) listboxEl.removeEventListener('scroll', onListboxScroll);
    selectEl.removeEventListener('change', syncTrigger);
    if (wrap.parentNode) {
      wrap.parentNode.insertBefore(selectEl, wrap);
      wrap.remove();
    }
    selectEl.classList.remove('pb-fcb-native');
    delete selectEl.dataset.pbFontCombobox;
  }

  return { destroy, syncTrigger, refresh };
}
